import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Link2, Check, X, UserPlus, AlertTriangle, Copy, Search, Shirt, ChevronDown, ClipboardList } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { BottomSheet, Empty, fmtData, BadgeFoto } from "@/components/Shared";
import { useRefreshOnPull } from "@/components/PullToRefresh";
import AnimatoForm from "@/components/AnimatoForm";
import { Switch } from "@/components/ui/switch";

export default function Segreteria() {
  const [richieste, setRichieste] = useState([]);
  const [richiesteGenitori, setRichiesteGenitori] = useState([]);
  const [storico, setStorico] = useState([]);
  const [impostazioni, setImpostazioni] = useState(null);
  const [dash, setDash] = useState(null);
  const [maglie, setMaglie] = useState(null);
  const [maglieAperte, setMaglieAperte] = useState(false);
  const [tab, setTab] = useState("attesa");
  const [nuovaOpen, setNuovaOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  // Code di revisione manuale prodotte dalle migrazioni del lotto 4
  const [revisione, setRevisione] = useState(null);

  const load = useCallback(() => {
    api.get("/iscrizioni/richieste?stato=in_attesa").then((r) => setRichieste(r.data));
    api.get("/genitori/richieste?stato=in_attesa").then((r) => setRichiesteGenitori(r.data)).catch(() => setRichiesteGenitori([]));
    api.get("/iscrizioni/richieste").then((r) => setStorico(r.data.filter((x) => x.stato !== "in_attesa")));
    api.get("/impostazioni").then((r) => setImpostazioni(r.data));
    api.get("/dashboard").then((r) => setDash(r.data));
    api.get("/maglie/conteggio").then((r) => setMaglie(r.data));
    api.get("/animati/da-revisionare").then((r) => setRevisione(r.data)).catch(() => setRevisione(null));
  }, []);

  useEffect(() => { load(); }, [load]);
  useRefreshOnPull(load);

  const linkPubblico = `${window.location.origin}/iscrizione`;

  const copiaLink = () => {
    navigator.clipboard.writeText(linkPubblico);
    toast.success("Link copiato! Condividilo con i genitori su WhatsApp");
  };

  const toggleIscrizioni = async (v) => {
    try {
      const { data } = await api.put("/impostazioni", { iscrizioni_aperte: v });
      setImpostazioni(data);
      toast.success(v ? "Iscrizioni dal link pubblico aperte" : "Iscrizioni dal link pubblico chiuse");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const approva = async (r) => {
    try {
      await api.post(`/iscrizioni/richieste/${r.id}/approva`);
      toast.success(`${r.nome} ${r.cognome} iscritto tra gli Animati`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const rifiuta = async (r) => {
    try {
      await api.post(`/iscrizioni/richieste/${r.id}/rifiuta`);
      toast.success("Richiesta rifiutata");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const approvaGenitore = async (r) => {
    try {
      await api.post(`/genitori/richieste/${r.id}/approva`);
      toast.success(`Animato approvato e collegato all'account di ${r.genitore?.nome || "la famiglia"}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const rifiutaGenitore = async (r) => {
    try {
      await api.post(`/genitori/richieste/${r.id}/rifiuta`, {});
      toast.success("Richiesta di iscrizione Animato rifiutata");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const iscrizioneDiretta = async (payload) => {
    setSaving(true);
    try {
      await api.post("/animati", payload);
      toast.success(`${payload.nome} ${payload.cognome} iscritto!`);
      setNuovaOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const limiteRaggiunto = dash && dash.tot_animati >= dash.max_animati;

  return (
    <div data-testid="segreteria-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900">Segreteria</h1>
          <p className="text-sm text-slate-500 font-semibold">Iscrizioni e approvazioni</p>
        </div>
        <button className="btn-primary" onClick={() => setNuovaOpen(true)} data-testid="iscrizione-diretta-btn">
          <UserPlus className="h-4 w-4" /> Iscrizione diretta
        </button>
      </div>

      {limiteRaggiunto && (
        <div className="rounded-3xl bg-red-50 border border-red-200 p-4 mb-5 flex items-center gap-3" data-testid="limite-raggiunto-banner">
          <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
          <p className="text-sm font-bold text-red-700">Limite massimo di {dash.max_animati} Animati raggiunto: tutte le iscrizioni sono bloccate.</p>
        </div>
      )}

      {/* Code prodotte dalle migrazioni: il sistema NON ha indovinato nulla al
          posto di una persona, ha solo segnalato cosa va guardato a mano. */}
      {revisione?.totale > 0 && (
        <div className="rounded-3xl bg-amber-50 border border-amber-200 p-5 mb-5" data-testid="revisione-banner">
          <p className="flex items-center gap-2 font-display font-extrabold text-slate-900 mb-1">
            <ClipboardList className="h-5 w-5 text-amber-600" />
            {revisione.totale} anagrafic{revisione.totale === 1 ? "a" : "he"} da revisionare
          </p>
          <p className="text-xs text-slate-600 font-semibold mb-3">
            Dati importati dal vecchio modulo. Apri la scheda in Animati, sistema quanto indicato
            e salva: il segnale sparisce da solo.
          </p>
          <div className="space-y-3">
            {[
              ["note", "Note da riclassificare", "Il vecchio campo «Note» è stato spostato nelle informazioni sanitarie in via prudenziale. Va letto e smistato fra sanitario e organizzativo."],
              ["consensi", "Consensi da riacquisire", "Il vecchio consenso foto era attivo per default, quindi non è un consenso valido. Va richiesto alla famiglia."],
              ["contatti", "Contatti da completare", "Il contatto di emergenza era un campo unico: mancano cognome, legame e numero verificato."],
            ].map(([chiave, titolo, spiega]) => {
              const elenco = revisione[chiave] || [];
              if (elenco.length === 0) return null;
              return (
                <div key={chiave} className="rounded-2xl bg-white border border-amber-200 p-3" data-testid={`revisione-${chiave}`}>
                  <p className="text-sm font-bold text-slate-800">{titolo} · {elenco.length}</p>
                  <p className="text-xs text-slate-500 font-semibold mb-2">{spiega}</p>
                  <p className="text-xs text-slate-600">
                    {elenco.slice(0, 12).map((a) => `${a.nome} ${a.cognome}`).join(" · ")}
                    {elenco.length > 12 && ` … e altri ${elenco.length - 12}`}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="card-app p-5 mb-5">
        <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-2xl bg-sky-100 text-sky-600 flex items-center justify-center shrink-0"><Link2 className="h-5 w-5" /></div>
            <div className="min-w-0">
              <p className="font-bold text-slate-800 text-sm">Link pubblico per i genitori</p>
              <p className="text-xs text-slate-400 truncate">{linkPubblico}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <button className="btn-secondary !py-2 text-xs" onClick={copiaLink} data-testid="copia-link-btn"><Copy className="h-3.5 w-3.5" /> Copia link</button>
            <div className="flex items-center gap-2">
              <Switch checked={!!impostazioni?.iscrizioni_aperte} onCheckedChange={toggleIscrizioni} data-testid="iscrizioni-switch" />
              <span className={`text-xs font-bold ${impostazioni?.iscrizioni_aperte ? "text-emerald-600" : "text-red-500"}`}>
                {impostazioni?.iscrizioni_aperte ? "Aperte" : "Chiuse"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {richiesteGenitori.length > 0 && (
        <div className="card-app p-5 mb-5" data-testid="richieste-genitori-panel">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <p className="font-display font-extrabold text-slate-900">Richieste di iscrizione Animati</p>
              <p className="text-xs text-slate-500 font-semibold mt-1">Gli account Genitore sono già attivi: qui approvi o rifiuti il collegamento del minore.</p>
            </div>
            <span className="badge-info">{richiesteGenitori.length}</span>
          </div>
          <div className="space-y-3">
            {richiesteGenitori.map((r) => {
              const g = r.genitore || {};
              const a = r.iscrizione || {};
              return (
                <div key={r.id} className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4" data-testid={`richiesta-genitore-${r.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-display font-extrabold text-slate-900">{g.nome} {g.cognome}</p>
                      <p className="text-xs text-slate-500 font-semibold">{r.email} · {g.telefono} · {g.relazione}</p>
                      <p className="text-sm text-slate-700 font-bold mt-2">Iscrizione: {a.nome} {a.cognome}</p>
                      <p className="text-xs text-slate-400 font-semibold">CF: {a.codice_fiscale} · Nato/a {fmtData(a.data_nascita)}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button type="button" className="btn-primary !py-2 !px-3 text-xs" onClick={() => approvaGenitore(r)} data-testid={`approva-genitore-${r.id}`}><Check className="h-3.5 w-3.5" /> Approva</button>
                      <button type="button" className="btn-ghost !py-2 !px-3 text-xs text-red-500" onClick={() => rifiutaGenitore(r)} data-testid={`rifiuta-genitore-${r.id}`}><X className="h-3.5 w-3.5" /> Rifiuta</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* MAGLIE DA ACQUISTARE — collassabile: da chiusa mostra solo il riepilogo */}
      {maglie && (() => {
        const totaleSenzaTaglia = maglie.ordine_gruppi.reduce((n, k) => n + maglie.gruppi[k].senza_taglia.length, 0);
        return (
        <div className="card-app mb-5 overflow-hidden" data-testid="maglie-panel">
          <button
            type="button"
            onClick={() => setMaglieAperte((v) => !v)}
            className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50/60 transition-colors"
            aria-expanded={maglieAperte}
            data-testid="maglie-toggle"
          >
            <Shirt className="h-4 w-4 text-amber-500 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-bold text-slate-800 text-sm">Maglie da acquistare</p>
              {!maglieAperte && (
                <p className="text-xs text-slate-400 font-semibold truncate">
                  {maglie.ordine_gruppi.map((k) => `${maglie.gruppi[k].totale} ${maglie.gruppi[k].etichetta.toLowerCase()}`).join(" · ")}
                </p>
              )}
            </div>
            {totaleSenzaTaglia > 0 && (
              <span className="badge-warn shrink-0 hidden sm:inline-flex items-center gap-1" data-testid="maglie-badge-mancanti">
                <AlertTriangle className="h-3 w-3" /> {totaleSenzaTaglia}
              </span>
            )}
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wide shrink-0">
              Totale <span className="font-display text-sm text-slate-900">{maglie.totale_maglie}</span>
            </p>
            <ChevronDown className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${maglieAperte ? "rotate-180" : ""}`} />
          </button>

          {/* Tre tipi di maglia diversi: vanno ordinati separatamente */}
          {maglieAperte && (
          <div className="space-y-3 px-4 pb-4">
            {maglie.ordine_gruppi.map((k) => {
              const g = maglie.gruppi[k];
              return (
                <div key={k} className="rounded-2xl border border-slate-100 p-3" data-testid={`maglie-gruppo-${k}`}>
                  <div className="flex items-baseline justify-between gap-2 mb-2">
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">{g.etichetta}</p>
                    <p className="text-xs font-bold text-slate-400">
                      <span className="font-display text-sm text-slate-900">{g.totale}</span> maglie
                    </p>
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    {maglie.taglie.map((t) => (
                      <div key={t} className={`rounded-xl p-2 text-center ${g.conteggio[t] > 0 ? "bg-slate-50" : "bg-slate-50/50"}`} data-testid={`maglia-${k}-${t}`}>
                        <p className={`font-display text-lg font-extrabold leading-none ${g.conteggio[t] > 0 ? "text-slate-900" : "text-slate-300"}`}>{g.conteggio[t]}</p>
                        <p className="text-[10px] font-extrabold text-slate-400 mt-1">{t}</p>
                      </div>
                    ))}
                  </div>
                  {g.senza_taglia.length > 0 && (
                    <p className="text-[11px] font-semibold text-amber-700 flex items-start gap-1.5 mt-2" data-testid={`maglie-mancanti-${k}`}>
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
                      <span>
                        {g.senza_taglia.length} senza taglia
                        {k === "animati" ? "" : " (da indicare nel Profilo)"}: {g.senza_taglia.slice(0, 4).join(", ")}
                        {g.senza_taglia.length > 4 ? ` e altri ${g.senza_taglia.length - 4}` : ""}
                      </span>
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          )}
        </div>
        );
      })()}

      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab("attesa")} className={tab === "attesa" ? "btn-primary !py-2" : "btn-ghost !py-2"} data-testid="tab-attesa">
          In attesa {richieste.length > 0 && <span className="rounded-full bg-white/30 px-2 text-xs">{richieste.length}</span>}
        </button>
        <button onClick={() => setTab("storico")} className={tab === "storico" ? "btn-primary !py-2" : "btn-ghost !py-2"} data-testid="tab-storico">Storico</button>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input type="search" className="input-app !pl-11" placeholder="Cerca per nome, cognome o codice fiscale..." value={search} onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }} onChange={(e) => setSearch(e.target.value)} data-testid="segreteria-search-input" />
      </div>

      {(() => {
        const q = search.trim().toLowerCase();
        const filtra = (arr) => q ? arr.filter((r) => `${r.nome} ${r.cognome} ${r.codice_fiscale}`.toLowerCase().includes(q)) : arr;
        const richFiltered = filtra(richieste);
        const storFiltered = filtra(storico);
        return tab === "attesa" ? (
          richFiltered.length === 0 ? (
            <Empty icon={Check} text={q ? "Nessuna richiesta corrisponde alla ricerca" : "Nessuna richiesta in attesa"} sub={q ? undefined : "Le nuove richieste dal link pubblico compariranno qui"} />
          ) : (
            <div className="space-y-3">
              {richFiltered.map((r) => (
                <div key={r.id} className="card-app p-5" data-testid={`richiesta-${r.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-display font-extrabold text-slate-900">{r.nome} {r.cognome}</p>
                      <p className="text-xs text-slate-400 font-semibold">CF: {r.codice_fiscale} · Taglia {r.taglia_maglia} · Nato/a {fmtData(r.data_nascita)}</p>
                      <p className="text-xs text-slate-400 font-semibold mt-0.5">
                        Contatti: {r.contatti_emergenza?.map((c) => [`${c.nome} ${c.cognome || ""}`.trim(), c.telefono || c.recapito].filter(Boolean).join(" · ")).join(" | ")}
                      </p>
                      {r.cf_duplicato && (
                        <span className="badge-neg inline-flex items-center gap-1 mt-2 mr-1.5"><AlertTriangle className="h-3 w-3" /> Possibile doppione (CF già presente)</span>
                      )}
                      <span className="inline-flex flex-wrap gap-1.5 mt-2">
                        <BadgeFoto animato={r} />
                        {r.info_sanitarie && (
                          <span className="badge-warn" title="Informazioni sanitarie conferite: visibili solo ai Responsabili">
                            Info sanitarie presenti
                          </span>
                        )}
                        {r.info_sanitarie_scartate && (
                          <span className="badge-neg" title="Il genitore ha scritto informazioni sanitarie ma non ha prestato il consenso: il testo non è stato conservato">
                            Info sanitarie non conservate
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button className="btn-primary !py-2 !bg-emerald-500 hover:!bg-emerald-600" onClick={() => approva(r)} data-testid={`approva-btn-${r.id}`}>
                        <Check className="h-4 w-4" /> Approva
                      </button>
                      <button className="btn-danger !py-2" onClick={() => rifiuta(r)} data-testid={`rifiuta-btn-${r.id}`}>
                        <X className="h-4 w-4" /> Rifiuta
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : storFiltered.length === 0 ? (
          <Empty icon={Check} text={q ? "Nessuna richiesta corrisponde alla ricerca" : "Nessuna richiesta gestita"} />
        ) : (
          <div className="space-y-2">
            {storFiltered.map((r) => (
              <div key={r.id} className="card-app p-4 flex items-center justify-between gap-3" data-testid={`storico-${r.id}`}>
                <div>
                  <p className="font-bold text-slate-800 text-sm">{r.nome} {r.cognome}</p>
                  <p className="text-xs text-slate-400">CF: {r.codice_fiscale}</p>
                </div>
                {r.stato === "approvata" ? <span className="badge-pos">Approvata</span> : <span className="badge-neg">Rifiutata</span>}
              </div>
            ))}
          </div>
        );
      })()}

      <BottomSheet open={nuovaOpen} onOpenChange={setNuovaOpen} title="Iscrizione diretta" description="La scheda Animato viene creata subito, senza approvazione" testId="iscrizione-diretta-sheet">
        <AnimatoForm onSubmit={iscrizioneDiretta} saving={saving} submitLabel="Iscrivi" />
      </BottomSheet>
    </div>
  );
}
